'use client'

import { authFetch } from '@/lib/auth/auth-fetch'
import { useWallet } from '@/providers/WalletContext'
import { captureApiError } from '@/utils/sentry-helpers'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { HyperliquidBalance } from './useHyperliquidBalance'

// ---------------------------------------------------------------------------
// Shared authed-fetch helper — Privy bearer auth replaces the old
// stampWhoami pattern; server routes resolve the user via resolveUser →
// requireAuthUser.
// ---------------------------------------------------------------------------

function useAuthedFetch() {
	const { authenticated } = useWallet()
	return {
		ready: authenticated,
		fetch: (input: RequestInfo | URL, init: RequestInit = {}) =>
			authFetch(input, {
				...init,
				headers: { 'Content-Type': 'application/json', ...init.headers },
			}),
	}
}

// ---------------------------------------------------------------------------
// Order placement
// ---------------------------------------------------------------------------

export type OrderInput = {
	outcome: number
	side: 0 | 1
	isBuy: boolean
	priceTick: string
	size: string
	tif: 'Gtc' | 'Ioc' | 'Alo'
	reduceOnly?: boolean
}

export function useHyperliquidOrder() {
	const qc = useQueryClient()
	const { fetch: authedFetch } = useAuthedFetch()
	return useMutation({
		mutationFn: async (input: OrderInput) => {
			await qc.refetchQueries({ queryKey: ['hl-balance'], type: 'active' })
			if (input.isBuy) {
				const fresh = qc.getQueryData<HyperliquidBalance>(['hl-balance'])
				const availableStr = fresh?.spot?.usdcAvailable
				const available = availableStr ? Number.parseFloat(availableStr) : Number.NaN
				const notional = Number.parseFloat(input.size) * Number.parseFloat(input.priceTick)
				if (
					Number.isFinite(available) &&
					Number.isFinite(notional) &&
					notional > available + 0.01
				) {
					throw new Error(
						`Insufficient balance: need $${notional.toFixed(2)} USDC, have $${available.toFixed(2)}`
					)
				}
			}
			const r = await authedFetch('/api/hyperliquid/order', {
				method: 'POST',
				body: JSON.stringify(input),
			})
			if (!r.ok) {
				const e = (await r.json().catch(() => ({}))) as { error?: string }
				throw new Error(e.error || `order ${r.status}`)
			}
			return r.json()
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['hl-balance'] })
			qc.invalidateQueries({ queryKey: ['hl-positions'] })
			qc.invalidateQueries({ queryKey: ['hl-open-orders'] })
		},
		onError: (error, variables) => {
			// Skip the friendly "Insufficient balance" client-side throws —
			// they're expected user-input outcomes, not bugs. Only capture
			// real failures (server reject, network error, HL exchange err).
			if (error instanceof Error && /insufficient balance/i.test(error.message)) return
			captureApiError(error, {
				endpoint: 'hyperliquid_order_hook',
				extra: {
					outcome: variables.outcome,
					side: variables.side,
					isBuy: variables.isBuy,
					size: variables.size,
					priceTick: variables.priceTick,
					tif: variables.tif,
				},
			})
		},
	})
}

// ---------------------------------------------------------------------------
// Cancel
// ---------------------------------------------------------------------------

export function useHyperliquidCancel() {
	const qc = useQueryClient()
	const { fetch: authedFetch } = useAuthedFetch()
	return useMutation({
		mutationFn: async (input: { outcome: number; side: 0 | 1; oid: number }) => {
			const r = await authedFetch('/api/hyperliquid/cancel', {
				method: 'POST',
				body: JSON.stringify(input),
			})
			if (!r.ok) {
				const e = (await r.json().catch(() => ({}))) as { error?: string }
				throw new Error(e.error || `cancel ${r.status}`)
			}
			return r.json()
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['hl-open-orders'] })
			qc.invalidateQueries({ queryKey: ['hl-positions'] })
		},
	})
}

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

export type HyperliquidPosition = {
	position: {
		coin: string
		szi: string
		entryPx?: string
		positionValue?: string
		unrealizedPnl?: string
		// Server-enriched: live mid-price for the share's coin from /info
		// allMids. Used by the UI as "Current" so positions don't display
		// the entry price masquerading as current.
		markPx?: string
	}
	type?: string
	// Server-enriched: real market title + asset icon parsed from
	// outcomeMeta. Renders in portfolio Positions / History instead of
	// the raw "#NNN" coin id.
	marketTitle?: string
	marketImageUrl?: string
}

export function useHyperliquidPositions() {
	const { ready, fetch: authedFetch } = useAuthedFetch()
	return useQuery({
		queryKey: ['hl-positions'],
		enabled: ready,
		refetchInterval: 15_000,
		queryFn: async (): Promise<HyperliquidPosition[]> => {
			const r = await authedFetch('/api/hyperliquid/positions')
			if (!r.ok) throw new Error(`positions ${r.status}`)
			const data = (await r.json()) as { positions: HyperliquidPosition[] }
			return data.positions
		},
	})
}

// ---------------------------------------------------------------------------
// Open orders
// ---------------------------------------------------------------------------

export type HyperliquidOpenOrder = {
	coin: string
	side: 'A' | 'B'
	limitPx: string
	sz: string
	oid: number
	timestamp: number
	// Enriched server-side via outcomeMeta (same source the positions
	// route uses) so rows render correct title + asset image even for
	// orders on events the user isn't currently viewing.
	marketTitle?: string
	marketImageUrl?: string
}

export function useHyperliquidOpenOrders() {
	const { ready, fetch: authedFetch } = useAuthedFetch()
	return useQuery({
		queryKey: ['hl-open-orders'],
		enabled: ready,
		refetchInterval: 15_000,
		queryFn: async (): Promise<HyperliquidOpenOrder[]> => {
			const r = await authedFetch('/api/hyperliquid/open-orders')
			if (!r.ok) throw new Error(`open-orders ${r.status}`)
			const data = (await r.json()) as { orders: HyperliquidOpenOrder[] }
			return data.orders
		},
	})
}

// ---------------------------------------------------------------------------
// L2 book (read-only, public route — no stamp needed)
// ---------------------------------------------------------------------------

export type L2Level = { px: string; sz: string; n: number }
export type L2Book = { coin: string; time: number; levels: [L2Level[], L2Level[]] }

export function useHyperliquidOrderBook(coin: string | null) {
	return useQuery({
		queryKey: ['hl-l2book', coin],
		enabled: Boolean(coin),
		refetchInterval: 10_000,
		queryFn: async (): Promise<L2Book> => {
			const r = await fetch(`/api/hyperliquid/orderbook/${encodeURIComponent(coin as string)}`)
			if (!r.ok) throw new Error(`orderbook ${r.status}`)
			return r.json()
		},
	})
}

// ---------------------------------------------------------------------------
// Fills (settled + non-settled trade history)
// ---------------------------------------------------------------------------

export type HyperliquidFill = {
	coin: string
	/**
	 * Weighted-average price across all fills for this oid (HL match engine
	 * can split a single user order across multiple book levels). Display
	 * as-is; the route already collapses identical-oid matches into one
	 * row with this avg.
	 */
	px: string
	/** Total filled size across all matches for this oid. */
	sz: string
	side: 'A' | 'B'
	/** Latest match timestamp within the order's fills. */
	time: number
	dir: string
	closedPnl: string
	hash: string
	oid: number
	fee: string
	feeToken: string
	tid: number
	// Server-enriched (see /api/hyperliquid/fills) — already populated for
	// every fill in the response, but typed optional to remain compatible
	// with code that constructs synthetic fills locally.
	marketTitle?: string
	marketImageUrl?: string
	/**
	 * Number of underlying matches collapsed into this row. Optional badge
	 * the UI can render (e.g. "BUY 71 @ 14.6¢ · 4 fills") so users can
	 * tell a multi-level fill apart from a single-level one without
	 * exposing the full match list.
	 */
	fills?: number
}

export function useHyperliquidFills() {
	const { ready, fetch: authedFetch } = useAuthedFetch()
	return useQuery({
		queryKey: ['hl-fills'],
		enabled: ready,
		refetchInterval: 15_000,
		queryFn: async (): Promise<HyperliquidFill[]> => {
			const r = await authedFetch('/api/hyperliquid/fills')
			if (!r.ok) throw new Error(`fills ${r.status}`)
			const data = (await r.json()) as { fills: HyperliquidFill[] }
			return data.fills
		},
	})
}

// ---------------------------------------------------------------------------
// Close position (opposite-side IOC, reduceOnly)
// ---------------------------------------------------------------------------

export function useHyperliquidClosePosition() {
	const placeOrder = useHyperliquidOrder()
	return {
		...placeOrder,
		closePosition: async (input: {
			outcome: number
			side: 0 | 1
			size: string // current position size, positive number
			topOfBookPrice: number // current best opposite-side price
		}) => {
			// Aggressive slippage for IOC close: 10% below top-of-book (floored
			// to tick size 0.001). Single-tick offset was too tight on thin books
			// — a small move between read and execution caused zero fills.
			const aggressivePrice = Math.max(0.001, input.topOfBookPrice * 0.9)
			const priceTick = (Math.floor(aggressivePrice * 1000) / 1000).toFixed(3)
			const result = await placeOrder.mutateAsync({
				outcome: input.outcome,
				side: input.side,
				isBuy: false,
				priceTick,
				size: input.size,
				tif: 'Ioc',
				reduceOnly: true,
			})
			const statuses = (result?.statuses ?? []) as Array<
				{ filled?: { totalSz: string } } | { resting?: unknown } | { error?: string }
			>
			const first = statuses[0]
			if (first && 'filled' in first) {
				const filledSz = Number.parseFloat(
					(first as { filled: { totalSz: string } }).filled.totalSz
				)
				const requestedSz = Number.parseFloat(input.size)
				if (filledSz < requestedSz * 0.5) {
					throw new Error(
						`Position only partially closed: ${filledSz} of ${requestedSz} shares filled. The remaining position is still open.`
					)
				}
			} else if (!first || 'resting' in first) {
				throw new Error(
					'Close order got zero fills — the book may have moved. Your position is still open.'
				)
			}
			return result
		},
	}
}
